import { Button, Checkbox } from '@ps/web-fusion-components';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import Body from '@swf/components/StarsModal/components/Body';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { useWidget } from '@swf/core/hooks/useWidget';
import { type Dispatch } from '@swf/core/store';
import type { PokerMultiTableTournamentsWidgetModelBase } from '@swf/poker';

import {
  getTournamentStorage,
  updateFavorites,
  updateTournamentStorage
} from '../../../utils/tournamentStorage';
import BaseHeader from '../BaseHeader/BaseHeader';

import {
  TXTCLI_Are_you_sure_you_want_to_remov_ELL_4,
  TXTCLI_Cancel,
  TXTCLI_DonXt_show_this_again,
  TXTCLI_Favorite_Tournaments,
  TXTCLI_Favorite_removal_confirmation,
  TXTCLI_OK,
  TXTCLI_Similar_games,
  TXTCLI_This_game_14,
  TXTCLI_Would_you_like_to_favorite_thi_ELL
} from './TournamentFavorites.messages.json';
import styles from './TournamentFavorites.scss';

interface SearchParams {
  widgetId: string;
  favReference: string;
  internalReference: string;
  isTournamentFavorite: string;
}

const TournamentFavorites = () => {
  const { favReference, internalReference, isTournamentFavorite } = useSearchParams<SearchParams>();
  const {
    state: { widgetId }
  } = useLocation<SearchParams>();
  const { modal } = useDispatch<Dispatch>();
  const { widgetDispatch } = useWidget<never, PokerMultiTableTournamentsWidgetModelBase>(widgetId);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const tournamentStorage = getTournamentStorage();
  const removeConfirmationState = tournamentStorage?.dontShowRemoveDialog ?? false;
  const showCheckbox = isTournamentFavorite && !removeConfirmationState;

  const updateFavoriteTournaments = useCallback(
    (updates: { similar?: string[]; specific?: string[] }) => {
      const similarTournaments = updates.similar ?? tournamentStorage?.similarFavorites ?? [];
      const specificTournaments = updates.specific ?? tournamentStorage?.specificFavorites ?? [];

      updateFavorites(similarTournaments, specificTournaments);

      // Update widget state
      widgetDispatch.setFavoriteTournaments({
        similar: similarTournaments,
        specific: specificTournaments
      });
    },
    [tournamentStorage, widgetDispatch]
  );

  const handlePrimaryClick = useCallback(() => {
    const currentStorage = getTournamentStorage();
    const handleSpecificFavorite = () => {
      if (internalReference) {
        widgetDispatch.addFavoriteTournament({
          type: 'specific',
          reference: internalReference
        });

        const currentSpecific = currentStorage?.specificFavorites ?? [];
        updateFavoriteTournaments({
          specific: [...currentSpecific, internalReference]
        });
      }
    };

    const handleRemoveFavorite = () => {
      if (internalReference || favReference) {
        updateTournamentStorage({
          dontShowRemoveDialog: !!checkboxRef.current?.checked
        });

        widgetDispatch.removeFavoriteTournament({
          similar: favReference,
          specific: internalReference
        });

        const currentSimilar = currentStorage?.similarFavorites ?? [];
        const currentSpecific = currentStorage?.specificFavorites ?? [];

        updateFavoriteTournaments({
          similar: currentSimilar.filter((ref: string) => ref !== favReference),
          specific: currentSpecific.filter((ref: string) => ref !== internalReference)
        });
      }
    };

    if (!isTournamentFavorite) {
      handleSpecificFavorite();
    } else {
      handleRemoveFavorite();
    }
    modal.close();
  }, [
    favReference,
    internalReference,
    isTournamentFavorite,
    modal,
    updateFavoriteTournaments,
    widgetDispatch
  ]);

  const handleSecondaryClick = useCallback(() => {
    const handleSimilarFavorite = () => {
      if (favReference) {
        widgetDispatch.addFavoriteTournament({
          type: 'similar',
          reference: favReference
        });

        const currentSimilar = tournamentStorage?.similarFavorites ?? [];
        updateFavoriteTournaments({
          similar: [...currentSimilar, favReference]
        });
      }
    };

    if (!isTournamentFavorite) {
      handleSimilarFavorite();
    }
    modal.close();
  }, [
    favReference,
    isTournamentFavorite,
    modal,
    tournamentStorage?.similarFavorites,
    updateFavoriteTournaments,
    widgetDispatch
  ]);

  return (
    <div className={styles.container}>
      <BaseHeader
        headerClassName={styles.modalHeader}
        title={
          <Translation
            {...(!isTournamentFavorite
              ? TXTCLI_Favorite_Tournaments
              : TXTCLI_Favorite_removal_confirmation)}
          />
        }
      />
      <Body className={styles.body}>
        <span className={styles.message}>
          <Translation
            {...(!isTournamentFavorite
              ? TXTCLI_Would_you_like_to_favorite_thi_ELL
              : TXTCLI_Are_you_sure_you_want_to_remov_ELL_4)}
          />
        </span>
        <div className={styles.bodyButtonActions}>
          <Button
            variant="primary"
            size="medium"
            className={styles.button}
            onClick={handlePrimaryClick}
          >
            <Translation {...(!isTournamentFavorite ? TXTCLI_This_game_14 : TXTCLI_OK)} />
          </Button>
          <Button
            variant="secondary"
            size="medium"
            className={classNames(styles.button, styles.similarGamesButton)}
            onClick={handleSecondaryClick}
          >
            <Translation {...(!isTournamentFavorite ? TXTCLI_Similar_games : TXTCLI_Cancel)} />
          </Button>
        </div>
        {showCheckbox && (
          <Checkbox size="small" className={styles.dontShowAgain} inputRef={checkboxRef}>
            <Translation {...TXTCLI_DonXt_show_this_again} tagName="p" />
          </Checkbox>
        )}
      </Body>
    </div>
  );
};

export default TournamentFavorites;
